import React from 'react';
import { ContactSection } from '@/components/sections/ContactSection';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Calendar,
  MessageCircle
} from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">Get In Touch</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Have questions or ready to schedule? Our team is here to help. Reach out to us 
            and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Phone className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">Call Us</h3>
                <a href="tel:321-500-4263" className="text-primary font-medium hover:underline">
                  321.500.HAND (4263)
                </a>
              </CardContent>
            </Card>

            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">Email Us</h3>
                <a href="mailto:info@melbournehandcenter.com" className="text-primary font-medium hover:underline text-sm">
                  info@melbournehandcenter.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">Office Hours</h3>
                <p className="text-muted-foreground text-sm">Mon-Fri: 8AM - 5PM</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">Book Online</h3>
                <a 
                  href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-medium hover:underline text-sm"
                >
                  Schedule Now
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <ContactSection />

      {/* Location Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl font-bold text-foreground mb-6">
                Visit Our Office
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Address</h3>
                    <p className="text-muted-foreground">2010 W. Eau Gallie Blvd</p>
                    <p className="text-muted-foreground">Suite 104</p>
                    <p className="text-muted-foreground">Melbourne, FL 32935</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Phone & Fax</h3>
                    <p className="text-muted-foreground">Phone: 321.500.HAND (4263)</p>
                    <p className="text-muted-foreground">Fax: 888.782.9622</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Office Hours</h3>
                    <p className="text-muted-foreground">Monday - Friday</p>
                    <p className="text-muted-foreground">8:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
              <Button variant="hero" size="lg" className="mt-8" asChild>
                <a 
                  href="https://maps.google.com/maps?q=2010+W+Eau+Gallie+Blvd+Melbourne+FL"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Get Directions
                </a>
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3520.6367693547776!2d-80.65148392358652!3d28.106947411867997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88de1234567890ab%3A0x0!2s2010%20W%20Eau%20Gallie%20Blvd%2C%20Melbourne%2C%20FL%2032935!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Melbourne Hand Center Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
